import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MiscellaneousServicesOutlinedIcon from "@mui/icons-material/MiscellaneousServicesOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { setCatalogServiceDetailsOnEvent } from "../../Redux/slices/addEnquirySlice";
import MUITypography from "../../Components/MUITypography";
import { VM_Colors } from "../../Styles/VMStyles";
import StyledInputField from "../../Components/Custom/StyledInputField";
import StorageFactory from "../../Util/StorageFactory";
import {
  SESSION_USER_COUNTRY_DATA_KEY,
  SESSION_USER_ID_KEY,
} from "../../Util/VMConstants";
import { formatAmount } from "../../Util/UtilHelper";
import {
  addService,
  deleteService,
  getServicesList,
  toServiceDetails,
  updateService,
} from "../../Services/additionalServicesService";

const emptyForm = {
  service_name: "",
  service_charge: "",
};

const apiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const toChargeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const catalogToSelection = (item) => toServiceDetails(item);

function AEEventServiceDetails({ tabIndex = 0 }) {
  const dispatch = useDispatch();
  const events = useSelector((state) => state.addEnquiry.events);
  const eventDetails = events[tabIndex]?.eventDetails || {};
  const subVenueId =
    eventDetails.sub_venue_id ||
    eventDetails.sub_venue?.sub_venue_id ||
    "";
  const subVenueName =
    eventDetails.sub_venue?.sub_venue_name ||
    (typeof eventDetails.sub_venue === "string" ? eventDetails.sub_venue : "") ||
    "";

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);
  const currencySymbol =
    sessionFactory.get(SESSION_USER_COUNTRY_DATA_KEY)?.currency_symbol || "₹";

  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadCatalog = useCallback(async () => {
    if (!userID || !subVenueId) {
      setCatalog([]);
      return;
    }
    setLoading(true);
    try {
      const result = await getServicesList({
        user_id: userID,
        sub_venue_id: subVenueId,
      });
      if (result.success) {
        const list = result.data.filter(
          (item) =>
            !item.sub_venue_id ||
            String(item.sub_venue_id) === String(subVenueId)
        );
        setCatalog(list);
        dispatch(
          setCatalogServiceDetailsOnEvent({
            index: tabIndex,
            services: list.map(catalogToSelection),
          })
        );
      } else {
        setCatalog([]);
        toast.error(result.message || "Failed to load services");
      }
    } catch (error) {
      setCatalog([]);
      toast.error(apiErrorMessage(error, "Failed to load additional services"));
    } finally {
      setLoading(false);
    }
  }, [userID, subVenueId, tabIndex, dispatch]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const filteredCatalog = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return catalog;
    return catalog.filter((item) =>
      String(item.service_name || "").toLowerCase().includes(query)
    );
  }, [catalog, search]);

  const catalogTotal = useMemo(
    () =>
      catalog.reduce(
        (sum, service) => sum + toChargeNumber(service.service_charge),
        0
      ),
    [catalog]
  );

  const openAddDialog = () => {
    setEditingService(null);
    setForm(emptyForm);
    setFormError("");
    setDialogOpen(true);
  };

  const openEditDialog = (item, event) => {
    event?.stopPropagation();
    setEditingService(item);
    setForm({
      service_name: item.service_name || "",
      service_charge: String(toChargeNumber(item.service_charge)),
    });
    setFormError("");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setEditingService(null);
    setForm(emptyForm);
    setFormError("");
  };

  const handleFormChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    if (formError) setFormError("");
  };

  const validateForm = () => {
    const name = form.service_name.trim();
    if (!name) return "service_name is required";
    if (form.service_charge === "" || form.service_charge == null) {
      return "service_charge is required";
    }
    const charge = Number(form.service_charge);
    if (!Number.isFinite(charge) || charge < 0) {
      return "service_charge must be a valid non-negative number";
    }
    return "";
  };

  const handleSaveService = async () => {
    if (!subVenueId) {
      setFormError("Select a sub venue first");
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const payload = {
      user_id: userID,
      sub_venue_id: subVenueId,
      service_name: form.service_name.trim(),
      service_charge: Number(form.service_charge),
    };

    setSaving(true);
    setFormError("");
    try {
      const result = editingService
        ? await updateService({
            id: editingService.id,
            ...payload,
          })
        : await addService(payload);

      if (result.success) {
        toast.success(
          result.message ||
            (editingService
              ? "Service updated successfully"
              : "Service added successfully")
        );
        await loadCatalog();
        setDialogOpen(false);
        setEditingService(null);
        setForm(emptyForm);
      } else {
        setFormError(result.message || "Unable to save service");
      }
    } catch (error) {
      setFormError(apiErrorMessage(error, "Unable to save service"));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteService({
        id: deleteTarget.id,
        user_id: userID,
      });
      if (result.success) {
        toast.success(result.message || "Service deleted successfully");
        setDeleteTarget(null);
        await loadCatalog();
      } else {
        toast.error(result.message || "Failed to delete service");
      }
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to delete service"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Paper sx={{ width: "100%", mt: 2, p: 2, backgroundColor: VM_Colors.white1 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          mb: catalog.length > 3 ? 1.5 : 2,
        }}
      >
        <Box>
          <MUITypography variant="h3">Additional Services (Optional)</MUITypography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {subVenueId
              ? `Services for ${subVenueName || "this sub venue"}`
              : "Select a sub venue to load its additional services"}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
          disabled={!subVenueId}
          sx={{ borderRadius: "8px", fontWeight: "bold", whiteSpace: "nowrap" }}
        >
          Add Service
        </Button>
      </Box>

      {(catalog.length > 3 || search.trim()) && (
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: VM_Colors.steel_gray }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            maxWidth: { sm: 360 },
            "& .MuiOutlinedInput-root": {
              backgroundColor: VM_Colors.white,
              borderRadius: "8px",
              "& fieldset": { borderColor: "#E4EAF2" },
              "&:hover fieldset": { borderColor: "#C9D3E4" },
              "&.Mui-focused fieldset": {
                borderColor: VM_Colors.primary,
                borderWidth: 1,
              },
            },
          }}
        />
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress size={26} />
        </Box>
      ) : !subVenueId ? (
        <Box
          sx={{
            py: 4.5,
            px: 2,
            textAlign: "center",
            borderRadius: "8px",
            backgroundColor: VM_Colors.white,
          }}
        >
          <MiscellaneousServicesOutlinedIcon
            sx={{ fontSize: 32, color: VM_Colors.steel_gray, mb: 1 }}
          />
          <Typography fontWeight={600} sx={{ mb: 0.5 }}>
            Select a sub venue first
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Additional services are different for each sub venue.
          </Typography>
        </Box>
      ) : catalog.length > 0 ? (
        <Box
          sx={{
            backgroundColor: VM_Colors.white,
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {filteredCatalog.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3.5, textAlign: "center" }}>
              No services match “{search.trim()}”.
            </Typography>
          ) : (
            filteredCatalog.map((item, index) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: { xs: 1.25, sm: 2 },
                    py: 1.25,
                    borderBottom:
                      index < filteredCatalog.length - 1 || catalog.length > 0
                        ? "1px solid #EEF2F7"
                        : "none",
                    "&:hover": { backgroundColor: "rgba(30, 58, 138, 0.03)" },
                    "@media (hover: hover)": {
                      "& .row-actions": { opacity: 0.35 },
                      "&:hover .row-actions": { opacity: 1 },
                    },
                  }}
                >
                  <Typography fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {item.service_name}
                  </Typography>
                  <Typography
                    fontWeight={600}
                    sx={{
                      whiteSpace: "nowrap",
                      minWidth: 96,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatAmount(item.service_charge, currencySymbol)}
                  </Typography>
                  <Box className="row-actions" display="flex" sx={{ ml: 0.5 }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={(event) => openEditDialog(item, event)}
                        sx={{ color: VM_Colors.primary }}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setDeleteTarget(item)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))
          )}

          {catalog.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
                px: { xs: 1.5, sm: 2 },
                py: 1.25,
                backgroundColor: VM_Colors.white1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {catalog.length} {catalog.length === 1 ? "service" : "services"}
              </Typography>
              <Typography fontWeight={700}>
                Total {formatAmount(catalogTotal, currencySymbol)}
              </Typography>
            </Box>
          )}
        </Box>
      ) : null}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1.5,
            fontWeight: 700,
          }}
        >
          {editingService ? "Update Service" : "Add Service"}
          <IconButton size="small" onClick={closeDialog} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box pt={0.5}>
            <StyledInputField
              heading="Service Name"
              name="service_name"
              required
              fullWidth
              value={form.service_name}
              onChange={handleFormChange("service_name")}
            />
            <StyledInputField
              heading="Service Charge"
              name="service_charge"
              required
              fullWidth
              type="number"
              value={form.service_charge}
              onChange={handleFormChange("service_charge")}
            />
            {formError ? (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {formError}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveService} disabled={saving}>
            {saving ? "Saving..." : editingService ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle fontWeight={700}>Delete Service</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Delete “{deleteTarget?.service_name}” from the catalog? Existing saved
            enquiries will keep this service.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default AEEventServiceDetails;
